export type PremiumCheckoutPlan = "monthly" | "yearly";

export type PremiumCheckoutPlanConfig = {
    plan: PremiumCheckoutPlan;
    planLabel: string;
    priceDisplay: string;
    subtitle: string;
    trialBadge: string | null;
    /** Stripe Price ID env var */
    stripePriceEnvKey: string;
    /** Amount charged now via Apple Pay (cents) */
    applePayAmountNow: number;
    applePayTotalLabel: string;
    applePayDisplayItems: { label: string; amount: number }[];
    disclaimer: string;
};

const TERMS_HREF =
    "https://instantheartrate.com/privacy-policy-and-terms-of-use#tos-section";
const PRIVACY_HREF =
    "https://instantheartrate.com/privacy-policy-and-terms-of-use#privacy-policy-section";

export const PREMIUM_CHECKOUT_PLANS: Record<
    PremiumCheckoutPlan,
    PremiumCheckoutPlanConfig
> = {
    monthly: {
        plan: "monthly",
        planLabel: "Monthly",
        priceDisplay: "$9.99/month",
        subtitle: "No commitment, cancel anytime",
        trialBadge: null,
        stripePriceEnvKey: "REACT_APP_STRIPE_999_MONTHLY_PRICE",
        applePayAmountNow: 999,
        applePayTotalLabel: "Instant Heart Rate Premium — Monthly",
        applePayDisplayItems: [
            { label: "Premium Monthly", amount: 999 },
        ],
        disclaimer:
            "This offer only valid for Apple devices. Subscription will automatically renew unless cancelled before the end of current period. Manage your subscription from instantheartrate.com. By proceeding, you agree to the Terms and Privacy Policy.",
    },
    yearly: {
        plan: "yearly",
        planLabel: "Yearly",
        priceDisplay: "$4.99/month",
        subtitle: "3 days free, then $59.99 per year",
        trialBadge: "3 Days Free",
        stripePriceEnvKey: "REACT_APP_STRIPE_5999_YEARLY_PRICE",
        applePayAmountNow: 0,
        applePayTotalLabel:
            "$0 now then $59.99 USD after 3-Day Trial. Cancel anytime.",
        applePayDisplayItems: [
            { label: "Charged Today", amount: 0 },
            { label: "Charge After 3-Day Free Trial", amount: 5999 },
        ],
        disclaimer:
            "This offer only valid for Apple devices. 3 day free trial available only to first-time subscribers. Subscription will automatically renew unless cancelled before the end of current period. Manage your subscription from instantheartrate.com. By proceeding, you agree to the Terms and Privacy Policy.",
    },
};

export const PREMIUM_CHECKOUT_LEGAL_LINKS = {
    terms: TERMS_HREF,
    privacy: PRIVACY_HREF,
} as const;

export function getStripePriceId(plan: PremiumCheckoutPlan): string | undefined {
    const key = PREMIUM_CHECKOUT_PLANS[plan].stripePriceEnvKey;
    const value = process.env[key];
    return value && value.trim() ? value.trim() : undefined;
}

export function maskEmailForBanner(raw: string): string {
    const email = raw.trim();
    const at = email.indexOf("@");
    if (at <= 0 || at === email.length - 1) return "";

    const local = email.slice(0, at);
    const domain = email.slice(at + 1);
    const maskedLocal = local.length > 10 ? `${local.slice(0, 10)}***` : local;
    return `${maskedLocal}@${domain}`.slice(0, 160);
}

/** Design: l****@gmail.com */
export function maskEmailForSyncHint(raw: string): string {
    const email = raw.trim();
    const at = email.indexOf("@");
    if (at <= 0 || at === email.length - 1) return "";

    const local = email.slice(0, at);
    const domain = email.slice(at + 1);
    const first = local.charAt(0) || "•";
    return `${first}****@${domain}`;
}

export function readEmailFromSearch(
    searchParams: URLSearchParams,
): string | null {
    const rawParam =
        searchParams.get("user_email") || searchParams.get("email");
    if (!rawParam) return null;

    const raw = decodeURIComponent(String(rawParam)).trim();
    if (!raw || raw === "null" || raw === "undefined") return null;
    return raw;
}
